import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Row, getItemWithExpiration } from '@/lib/utils';
import { useLockerZones, useCreateLockerZone, useLockerStatus } from '@/features/locker/hooks';
import { readLockerReveal, writeLockerReveal } from '@/features/locker/lockerReveal';
import { colors } from '@/lib/colors';
import BusinessInfoFooter from '@/components/BusinessInfoFooter';
import MemberDetailTabBar, { MemberDetailTabItem } from '@/components/MemberProfile/MemberDetailTabBar';
import LockerAssignmentTab from './LockerAssignmentTab';
import LockerHistoryTab from './LockerHistoryTab';
import { LockerError, LockerLoading } from './LockerStates';

const TABS: MemberDetailTabItem[] = [
    { key: 'grid', label: '배정 현황' },
    { key: 'history', label: '회수 기록' },
];

const TAB_KEYS = TABS.map((tab) => tab.key);

export default function LockerManagementPage({
    createAlert,
    createTwoButtonAlert,
}: {
    createAlert?: any;
    createTwoButtonAlert?: any;
}) {
    const [auth] = useState(getItemWithExpiration('authObject'));
    const centerId = auth?.centerId;

    /*
     * 센터 열람 계정은 서버가 쓰기를 막고, 클라이언트도 shouldBlockCenterViewRequest 로
     * POST/PUT/DELETE 를 끊는다. 편집 UI 를 보여 주고 저장만 실패하게 하면 안 되므로
     * 쓰기 동작을 아예 감춘다. (PayrollAuthGuard 와 같은 판정식을 쓴다.)
     */
    const readOnly = auth?.accessMode === 'view' || auth?.role === 'center_viewer';

    // ─────────────────────────── 탭 (URL 로 둔다) ───────────────────────────
    /*
     * /locker 는 KeepAlive 대상이 아니라 라우트를 떠나면 상태가 통째로 초기화된다.
     * 탭을 URL 에 두면 새로고침·뒤로가기·링크 공유가 모두 그대로 살아난다.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'grid';

    /** 일괄 입력에 저장 대기·실패가 남아 있는지. 탭을 떠나면 그 입력이 사라진다. */
    const [bulkDirty, setBulkDirty] = useState(false);

    /**
     * 칸별 저장 큐를 **페이지가 소유한다.**
     *
     * 탭 전환은 배정 현황 탭의 **언마운트**다. 큐를 탭이 들고 있으면 그때 통째로 사라져,
     * 「전송 중 → 다른 탭 → 복귀 → 같은 칸 재입력」이면 옛 PUT 과 새 PUT 이 다시 병렬이 된다.
     * 슬롯 쓰기는 버전 조건 없는 upsert 라 늦게 도착한 옛 값이 이긴다.
     * 페이지가 들고 있으면 재진입 요청이 옛 요청 뒤에 줄을 선다.
     */
    const slotSaveChainsRef = useRef<Record<number, Promise<void>>>({});

    const applyTab = useCallback(
        (key: string) => {
            const next = new URLSearchParams(searchParams);
            next.set('tab', key);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    /*
     * beforeunload 는 새로고침·탭 닫기만 막는다. **앱 안에서의 탭 전환**은 그것으로 못 막아
     * 저장 대기 중인 입력이 조용히 사라졌다. 여기서 한 번 물어본다.
     */
    const handleTabChange = useCallback(
        (key: string) => {
            if (bulkDirty && createTwoButtonAlert) {
                createTwoButtonAlert(
                    '아직 저장되지 않은 칸이 있습니다',
                    '다른 탭으로 넘어가면 저장 대기 중이거나 실패한 입력이 사라집니다.',
                    true,
                    '계속 입력',
                    '넘어가기',
                    () => {},
                    () => applyTab(key),
                );
                return;
            }
            applyTab(key);
        },
        [applyTab, bulkDirty, createTwoButtonAlert],
    );

    // ─────────────────────────── 구역 ───────────────────────────
    const {
        data: zones = [],
        isLoading: zonesLoading,
        isError: zonesError,
        isSuccess,
        refetch: refetchZones,
    } = useLockerZones({ centerId });
    const createZoneMutation = useCreateLockerZone();
    const initializingRef = useRef(false);

    const defaultZoneId = zones.length > 0 ? zones[0]._id : null;

    /*
     * 구역이 없으면 기본 구역을 자동으로 만든다.
     *
     * 이 effect 는 **페이지에** 둔다. 탭 컴포넌트로 내리면 탭을 오갈 때마다 다시 마운트되어
     * 구역이 여러 개 생길 수 있다. initializingRef 는 같은 마운트 안에서의 중복 실행을 막는다.
     * 열람 계정은 쓰기가 막혀 있으므로 시도조차 하지 않는다 — 시도하면 클라이언트가
     * window.alert 를 띄운다.
     */
    useEffect(() => {
        if (readOnly) return;
        if (isSuccess && zones.length === 0 && !initializingRef.current) {
            initializingRef.current = true;
            createZoneMutation.mutate({
                centerId,
                body: { name: '기본 구역', totalCount: 500, startNumber: 1 },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zones, isSuccess, readOnly]);

    const {
        data: statusData,
        isLoading: statusLoading,
        isError: statusError,
        refetch: refetchStatus,
    } = useLockerStatus({
        centerId,
        zoneId: defaultZoneId || '',
        // 회수 기록 탭에서는 500칸 현황을 받을 이유가 없다. 탭을 열자마자 큰 응답을 끌어오지 않는다.
        enabled: !!defaultZoneId && activeTab === 'grid',
    });

    // ─────────────────────────── 비밀번호 공개 ───────────────────────────
    /*
     * 두 탭이 같은 공개 상태를 본다. 배정 현황에서 켜고 회수 기록으로 넘어갔는데 다시 가려지면
     * 장부 대조가 끊긴다. 기본값은 언제나 꺼짐이고 로그아웃 시 제거된다(lib/utils.ts).
     */
    const [revealAll, setRevealAll] = useState(() => readLockerReveal(centerId));
    useEffect(() => {
        setRevealAll(readLockerReveal(centerId));
    }, [centerId]);

    const handleToggleReveal = useCallback(() => {
        setRevealAll((prev) => {
            const next = !prev;
            writeLockerReveal(centerId, next);
            return next;
        });
    }, [centerId]);

    // 구역이 아직 없고 만드는 중이면 그리드도 로딩으로 본다. 빈 상태를 잠깐 보여주면 오해를 준다.
    const gridLoading = useMemo(
        () => zonesLoading || (!defaultZoneId && (createZoneMutation.isLoading || !isSuccess)) || statusLoading,
        [createZoneMutation.isLoading, defaultZoneId, isSuccess, statusLoading, zonesLoading],
    );

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' }}>
            <Col className="w-full px-[48px] pt-[36px] pb-[48px]" style={{ gap: 20 }}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <Col style={{ gap: 4 }}>
                        <span style={{ fontSize: 24, fontWeight: 700, color: colors.LAVEL_4 }}>락커 관리</span>
                        <span style={{ fontSize: 13, color: colors.LAVEL_1 }}>
                            칸마다 고정 비밀번호를 두고, 회원에게는 개별 비밀번호를 따로 줄 수 있습니다.
                        </span>
                    </Col>
                    {readOnly && (
                        <span
                            style={{
                                padding: '8px 14px',
                                borderRadius: 8,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: colors.GREY,
                                color: colors.LAVEL_1,
                                border: `1px solid ${colors.WHITE_600}`,
                            }}
                        >
                            열람 전용 계정입니다. 배정·회수·비밀번호 변경은 할 수 없습니다.
                        </span>
                    )}
                </Row>

                <MemberDetailTabBar
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    ariaLabel="락커 관리 메뉴"
                    idPrefix="locker"
                    variant="pill"
                />

                <div
                    role="tabpanel"
                    id={`locker-panel-${activeTab}`}
                    aria-labelledby={`locker-tab-${activeTab}`}
                    tabIndex={-1}
                >
                    {zonesError ? (
                        <LockerError title="라커 구역을 불러오지 못했습니다" onRetry={() => refetchZones()} />
                    ) : !centerId ? (
                        <LockerError title="센터 정보를 확인할 수 없습니다" detail="다시 로그인해 주세요" />
                    ) : activeTab === 'history' ? (
                        <LockerHistoryTab centerId={centerId} revealAll={revealAll} onToggleReveal={handleToggleReveal} />
                    ) : gridLoading ? (
                        <LockerLoading />
                    ) : (
                        <LockerAssignmentTab
                            centerId={centerId}
                            zoneId={defaultZoneId}
                            statusData={statusData}
                            isLoading={false}
                            isError={statusError}
                            onRetry={() => refetchStatus()}
                            readOnly={readOnly}
                            revealAll={revealAll}
                            onToggleReveal={handleToggleReveal}
                            onDirtyChange={setBulkDirty}
                            saveChainsRef={slotSaveChainsRef}
                            createAlert={createAlert}
                            createTwoButtonAlert={createTwoButtonAlert}
                        />
                    )}
                </div>
            </Col>

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
}
